import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import {
  X, Plus, MessageSquare, Calendar, Loader2,
  ExternalLink, ChevronLeft, ChevronRight, Search, Filter
} from "lucide-react";
import { GBP } from "../../../../types/Listing";
import { useLocationPosts } from "../../../../hooks/useLocationPosts";
import MediaCarousel from "./MediaCarousel";
import CreateForm from "./CreateForm";
import { POSTS_PER_PAGE, topicTypeLabel } from "./constants";
import { formatDate } from "./utils";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  gbp: GBP | null;
  onAdd: () => void;
  subjectEmail: string; 
}

const PostsModal: React.FC<Props> = ({ isOpen, onClose, gbp, onAdd ,subjectEmail}) => {
  const { data: posts = [], isLoading } = useLocationPosts(gbp, isOpen,subjectEmail);

  const modalRef = useRef<HTMLDivElement>(null);

  const [view, setView] = useState<"list" | "create">("list");
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState<string>("ALL");
  const [currentPage, setCurrentPage] = useState(1);

  const normalizedSearch = searchTerm.toLowerCase();

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterType, posts]);

  const filteredPosts = useMemo(() => {
    return posts.filter(post => {
      const summary = post.summary?.toLowerCase() || "";
      const title = post.event?.title?.toLowerCase() || "";

      const matchesSearch =
        !searchTerm ||
        summary.includes(normalizedSearch) ||
        title.includes(normalizedSearch);

      const matchesType =
        filterType === "ALL" || post.topicType === filterType;

      return matchesType && matchesSearch;
    });
  }, [posts, normalizedSearch, filterType, searchTerm]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredPosts.length / POSTS_PER_PAGE)
  );

  const paginatedPosts = useMemo(() => {
    const start = (currentPage - 1) * POSTS_PER_PAGE;
    return filteredPosts.slice(start, start + POSTS_PER_PAGE);
  }, [filteredPosts, currentPage]);

  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    if (isOpen) document.addEventListener("mousedown", handleOutsideClick);
    return () =>
      document.removeEventListener("mousedown", handleOutsideClick);
  }, [isOpen, onClose]);

  if (!isOpen || !gbp) return null;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm">
      <div
        ref={modalRef}
        className="bg-slate-50 w-full max-w-2xl h-[85vh] rounded-[2.5rem] shadow-2xl overflow-hidden flex flex-col"
      >
         
         <div className="bg-white px-8 py-5 border-b border-slate-100 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-20 h-9 bg-blue-600 rounded-2xl flex items-center justify-center text-white shadow-lg shadow-blue-100"><MessageSquare size={22} /></div>
              <div>
                <h2 className="text-lg font-black text-slate-900">Publications Google</h2>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{gbp.name}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {view === "list" && (
                <button onClick={() => setView("create")} className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-xl text-[11px] font-black uppercase hover:bg-blue-700 transition-all shadow-md"><Plus size={14} /> Nouveau</button>
              )}
              <button onClick={onClose} className="p-2.5 hover:bg-slate-100 rounded-xl transition-all text-slate-400"><X size={20} /></button>
            </div>
          </div>

          {view === "list" && (
            <div className="flex gap-3 items-center">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                <input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Rechercher une annonce..." className="w-full pl-11 pr-4 py-3 bg-slate-50 border-none rounded-2xl text-xs font-medium focus:ring-2 focus:ring-blue-100 transition-all outline-none" />
              </div>
 
              <select value={filterType} onChange={e => setFilterType(e.target.value)} className="bg-slate-50 border-none rounded-2xl px-4 py-3 text-[10px] font-black uppercase text-slate-500 outline-none focus:ring-2 focus:ring-blue-100">
                <option value="ALL">Tous les types</option>
                <option value="STANDARD">Actualités</option>
                <option value="EVENT">Événements</option>
                <option value="OFFER">Offres</option>
              </select>
            </div>
          )}
        </div>

         <div className="flex-1 overflow-y-auto p-8 custom-scrollbar">
          {view === "create" ? (
            <CreateForm onBack={() => setView("list")} onSubmit={(d) => { console.log(d); onAdd(); setView("list"); }} />
          ) : (
            <div className="space-y-6">
              {isLoading ? (
                <div className="flex flex-col items-center justify-center py-20 gap-4">
                  <Loader2 className="animate-spin text-blue-600" size={40} />
                  <span className="text-xs font-black text-slate-400 uppercase tracking-widest">Récupération des posts...</span>
                </div>
              ) : paginatedPosts.length === 0 ? (
                <div className="text-center py-20 bg-white rounded-[2rem] border-2 border-dashed border-slate-200">
                    <Filter className="mx-auto mb-4 text-slate-200" size={48} />
                    <p className="text-sm font-bold text-slate-400 uppercase tracking-widest">Aucun résultat trouvé</p>
                </div>
              ) : (
                paginatedPosts.map((post, i) => {
                  const type = topicTypeLabel[post.topicType ?? ""] ?? { label: "Post", color: "bg-slate-100 text-slate-600 border-slate-200" };

                  const media = (post.media ?? [])
  .map((m: any) => {
    const raw = m.thumbnailUrl ?? m.googleUrl ?? "";
    const url = raw.includes("googleusercontent") && !raw.includes("=")
      ? `${raw}=s800`
      : raw;

    return {
      url,
      isVideo: m.mediaFormat === "VIDEO"
    };
  })
  .filter(m => m.url);
                  return (
                    <div key={post.name ?? i} className="bg-white rounded-[2rem] border border-slate-200 overflow-hidden hover:shadow-xl hover:border-blue-200 transition-all duration-300 group">
                      {media.length > 0 && <MediaCarousel items={media} />}
                      <div className="p-7">
                        <div className="flex items-center justify-between mb-4">
                          <span className={`px-3 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest border ${type.color}`}>{type.label}</span>
                          <span className="flex items-center gap-1.5 text-[10px] font-bold text-slate-400 uppercase tracking-tighter"><Calendar size={14} /> {formatDate(post.createTime)}</span>
                        </div>
                        {post.event?.title && <h3 className="text-base font-black text-slate-900 mb-2 leading-tight">{post.event.title}</h3>}
                        <p className="text-sm text-slate-600 leading-relaxed italic line-clamp-4">"{post.summary}"</p>
                        <div className="mt-6 pt-6 border-t border-slate-50 flex items-center justify-between">
                            <a href={post.searchUrl} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-[10px] font-black uppercase text-blue-600 hover:text-blue-800 transition-colors"><ExternalLink size={14} /> Voir sur Google</a>
                            {post.callToAction && <div className="px-4 py-1.5 bg-slate-50 rounded-full text-[9px] font-bold text-slate-500 uppercase">{post.callToAction.actionType}</div>}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>

         {view === "list" && !isLoading && totalPages > 1 && (
          <div className="px-8 py-5 bg-white border-t border-slate-100 flex items-center justify-between">
            <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Page {currentPage} sur {totalPages}</span>
            <div className="flex gap-2">
              <button disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)} className="w-10 h-10 flex items-center justify-center rounded-xl border border-slate-200 disabled:opacity-20 hover:bg-slate-50 transition-all"><ChevronLeft size={18} /></button>
              <button disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)} className="w-10 h-10 flex items-center justify-center rounded-xl border border-slate-200 disabled:opacity-20 hover:bg-slate-50 transition-all"><ChevronRight size={18} /></button>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default PostsModal;